import React, { Component, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { includes, map, reject, pathOr } from 'ramda';

import { propTypes } from '../../util/types';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  FavoriteListingCard,
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { queryMyFavoritesListings } from './index.duck';
import css from './styles.css';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { isNilOrEmpty } from '../../util/validators';
import { LocationSearchForm } from '../../forms';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { withRouter } from 'react-router-dom';

const { UUID } = sdkTypes;

const MyFavoritesPageComponent = (props) => {
  const {
    closingListing,
    listings,
    openingListing,
    queryInProgress,
    queryListingsError,
    scrollingDisabled,
    intl,
    history,
    queryListingsSuccess,
    onUpdateCurrentProfile,
    myFavorites = [],
    currentUserHasListings,
  } = props;
  const [listingMenuOpen, setListingMenuOpen] = useState(null);
  const onToggleMenu = (listing) => {
    setListingMenuOpen(listing);
  };
  const handleRemoveListingFromFavorite = (listing) => {
    const isInMyFavorites = includes(listing.id.uuid, myFavorites);
    if (!isInMyFavorites) return;

    const profileUpdated = {
      protectedData: {
        myFavorites: reject(fav => fav ===  listing.id.uuid, myFavorites),
      },
    };
    onUpdateCurrentProfile(profileUpdated);
  };
  const listingsAreLoaded = !queryInProgress && queryListingsSuccess;

  const loadingResults = (
    <h2>
      <FormattedMessage id="ManageListingsPage.loadingOwnListings"/>
    </h2>
  );

  const queryError = (
    <h2 className={css.error}>
      <FormattedMessage id="ManageListingsPage.queryError"/>
    </h2>
  );

  const handleSearchSubmit = values => {
    const { search, selectedPlace } = values.location;
    const { origin, bounds } = selectedPlace;
    const searchParams = { address: search, origin, bounds };
    history.push(
      createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams)
    );
  };
  const noResults =
    listingsAreLoaded && isNilOrEmpty(listings) ? (
      <div className={css.rootNoResults}>
        <div className={css.contentNoResults}>
          {/*<div className={css.numberNoResults}>404</div>*/}
          <h1 className={css.headingNoResults}>
            <FormattedMessage id="MyFavoritesPage.noResultsHeading" />
          </h1>
          <p className={css.descriptionNoResults}>
            <FormattedMessage id="MyFavoritesPage.noResultsDescription" />
          </p>
          <LocationSearchForm className={css.searchFormNoResults} onSubmit={handleSearchSubmit} />
        </div>
      </div>
    ) : null;
  const title = intl.formatMessage({ id: 'MyFavoritesPage.title' });

  const panelWidth = 62.5;
  // Render hints for responsive image
  const renderSizes = [
    `(max-width: 767px) 100vw`,
    `(max-width: 1920px) ${panelWidth / 2}vw`,
    `${panelWidth / 3}vw`,
  ].join(', ');

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="MyFavoritesPage"/>
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          {queryInProgress ? loadingResults : null}
          {queryListingsError ? queryError : null}
          <div className={css.listingPanel}>
            {noResults}
            <div className={css.listingCards}>
              {listings.map(l => (
                <FavoriteListingCard
                  className={css.listingCard}
                  key={l.id.uuid}
                  listing={l}
                  isMenuOpen={!!listingMenuOpen && listingMenuOpen.id.uuid === l.id.uuid}
                  actionsInProgressListingId={openingListing || closingListing}
                  onToggleMenu={onToggleMenu}
                  handleRemoveListingFromFavorite={handleRemoveListingFromFavorite}

                  renderSizes={renderSizes}
                />
              ))}
            </div>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer/>
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};


MyFavoritesPageComponent.defaultProps = {
  listings: [],
  queryListingsError: null,
  queryParams: null,
  closingListing: null,
  closingListingError: null,
  openingListing: null,
  openingListingError: null,
};

const { arrayOf, bool, func, object, shape, string } = PropTypes;

MyFavoritesPageComponent.propTypes = {
  closingListing: shape({ uuid: string.isRequired }),
  listings: arrayOf(propTypes.ownListing),
  queryInProgress: bool.isRequired,
  queryListingsError: propTypes.error,
  queryParams: object,
  scrollingDisabled: bool.isRequired,
  currentUserHasListings: bool.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    queryInProgress,
    queryListingsError,
    queryListingsSuccess,
  } = state.MyFavoritesPage;
  const { currentUser, currentUserHasListings } = state.user;
  const myFavorites =
    pathOr([], ['attributes', 'profile', 'protectedData', 'myFavorites'], currentUser);
  const listings = getListingsById(state, map((fav) => new UUID(fav), myFavorites));

  return {
    myFavorites,
    currentPageResultIds,
    listings,
    queryInProgress,
    queryListingsSuccess,
    queryListingsError,
    scrollingDisabled: isScrollingDisabled(state),
    currentUserHasListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateCurrentProfile: (data) => dispatch(updateProfile(data)),
});

const MyFavoritesPage = compose(withRouter, connect(mapStateToProps, mapDispatchToProps), injectIntl)(
  MyFavoritesPageComponent,
);

MyFavoritesPage.loadData = () => {
  return queryMyFavoritesListings();
};

export default MyFavoritesPage;



// WEBPACK FOOTER //
// ./src/containers/MyFavoritesPage/index.js